import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-03-13 15:47:48 +0100 (Fri, 13 Mar 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 161511 $
//  Purpose   : i3f support
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Loader, Menu, Dropdown, Icon, Segment, Rail, Table, Message } from 'semantic-ui-react';
import { JSONTree } from 'uix/chunks';
// ---Framework imports
import { Sel } from 'uix/registries';
import { XMenu, HeaderSegment } from 'uix/components';
import { get, dict, uniq, strip, size, isArray, isString, flatten } from 'ldash';
import { funcPart } from 'uix/parts';
import { useState, useSelector, useEffect, usePersistentShallow } from 'uix/hooks';
import log from 'uix/logging';
import { useTypeInfo } from 'uix/types/hooks';
// ---Local imports
import styles from './i3f.css';
import { useI3F } from './hooks';
const I3FImage = ({ server, id, region = 'full', size = 1000, rotation = 0, quality = 'native', format = 'jpg' })=>/*#__PURE__*/ _jsx("img", {
        src: server + '/' + id + '/' + region + '/' + size + '/' + rotation + '/' + quality + '.' + format
    });
// VL/I3F-Routing
const MANIFEST = ({ id, server = '', version = 'v21' })=>`${server}/i3f/${version}/${id}/manifest`;
const SOME_DEFAULTS = [
    'https://visuallibrary.net/i3f/v20/3/manifest',
    'https://ulbhalvd.rc.vls.io/i3f/v20/8848994/manifest',
    'https://api.digitale-sammlungen.de/iiif/presentation/v2/bsb00095783/manifest'
];
export const getMembersFlat = (parent, level, path = [])=>flatten((parent.members || []).map((e)=>e['@type'] == 'sc:Canvas' ? {
            ...e,
            pid: parent['@id'],
            level,
            parent,
            path
        } : getMembersFlat(e, level + 1, [
            ...path,
            e['@id']
        ])));
const Summary = ({ i3fdata })=>{
    let lang = useSelector(Sel.language);
    let build = ({ label, value })=>{
        let decode_lang = (s)=>{
            if (isArray(s)) {
                let found = s.filter((e)=>e['@language'] == lang);
                if (found.length > 0) {
                    return found[0]['@value'];
                } else {
                    return s[0]['@value'] || 'no @value';
                }
            }
            return s;
        };
        //~ log(label, value)
        let l = decode_lang(label);
        let v = decode_lang(value);
        if (isString(v) && v.includes('<') && v.includes('>')) {
            v = /*#__PURE__*/ _jsx("span", {
                dangerouslySetInnerHTML: {
                    __html: v
                }
            });
        }
        return /*#__PURE__*/ _jsxs(Table.Row, {
            children: [
                /*#__PURE__*/ _jsx(Table.Cell, {
                    style: {
                        width: '90px'
                    },
                    children: l
                }),
                /*#__PURE__*/ _jsx(Table.Cell, {
                    children: v
                })
            ]
        }, l);
    };
    let rowsMetadata = get(i3fdata, 'metadata', []).map((e)=>build(e));
    let canvases = get(i3fdata, 'sequences[0].canvases', []);
    let structures = get(i3fdata, 'structures', []);
    let toc_count = structures.length;
    // v21
    let rangeMembers = get(i3fdata, 'structures[0].members[0]');
    if (rangeMembers) {
        let flat = getMembersFlat(rangeMembers, 0);
        //~ pages = flat.length
        toc_count = uniq(flat.map((e)=>e.pid)).length;
    }
    let calculated = [
        [
            'TOC',
            toc_count
        ],
        [
            'Canvases',
            canvases.length
        ],
        [
            'Members',
            String(Boolean(rangeMembers))
        ]
    ];
    let rowsCalculated = calculated.map(([label, value])=>build({
            label,
            value
        }));
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Table, {
                definition: true,
                compact: true,
                children: /*#__PURE__*/ _jsx(Table.Body, {
                    children: rowsMetadata
                })
            }),
            /*#__PURE__*/ _jsx(Table, {
                definition: true,
                compact: true,
                children: /*#__PURE__*/ _jsx(Table.Body, {
                    children: rowsCalculated
                })
            })
        ]
    });
};
const IMenu = ({ manifest, manifests, setManifest, setManifests, options, setOptions })=>{
    let apiurl = useSelector(Sel.apiurl);
    let vOptions = [
        'v20',
        'v21'
    ].map((m)=>({
            key: m,
            text: m,
            value: m
        }));
    let mOptions = manifests.map((m)=>({
            key: m,
            text: m,
            value: m
        }));
    let handleAddition = (e, { value })=>setManifests(uniq([
            ...manifests,
            strip(value)
        ]));
    let handleChange = (e, { value })=>setManifest(strip(value));
    return /*#__PURE__*/ _jsxs(XMenu, {
        children: [
            /*#__PURE__*/ _jsx(Menu.Item, {
                icon: "download",
                title: `open raw ${apiurl.href(manifest)}`,
                onClick: ()=>window.open(apiurl.href(manifest), 'i3f')
            }),
            /*#__PURE__*/ _jsx(Menu.Item, {
                icon: "external alternate",
                title: "open in i3f.vls.io",
                onClick: ()=>window.open(`https://i3f.vls.io/?id=${encodeURIComponent(apiurl.baseUrl(manifest))}`, 'i3f.vls.io')
            }),
            /*#__PURE__*/ _jsx(Menu.Item, {
                children: /*#__PURE__*/ _jsx(Dropdown, {
                    options: vOptions,
                    value: options.version,
                    onChange: (e, { value })=>setOptions({
                            version: value
                        })
                })
            }),
            /*#__PURE__*/ _jsx(Dropdown, {
                options: mOptions,
                placeholder: "Choose Manifest",
                search: true,
                selection: true,
                allowAdditions: true,
                value: manifest,
                onAddItem: handleAddition,
                onChange: handleChange
            })
        ]
    });
};
const I3FView = ({ i18n })=>{
    let [manifest, setManifest] = useState(null);
    let [options, setOptions] = usePersistentShallow('i3fopts', {
        version: 'v20'
    });
    let request = useSelector(Sel.request);
    let m = {
        id: request.id,
        version: options.version
    };
    let typeInfo = useTypeInfo(request.id);
    useEffect(()=>setManifest(null), [
        request.id,
        options
    ]);
    if (!manifest) {
        let enabled = get(typeInfo, 'cfg.i3f.on');
        if (enabled) {
            manifest = MANIFEST(m);
        }
    }
    let [manifests, setManifests] = useState(SOME_DEFAULTS);
    manifests = uniq([
        ...manifests,
        manifest
    ]);
    let data = useI3F({
        manifest
    });
    let shouldExpandNode = (keyName, data, level)=>{
        return level == 1;
    };
    let valueRenderer = (raw, value, ...path)=>raw;
    let menu = /*#__PURE__*/ _jsx(IMenu, {
        manifest,
        setManifest,
        manifests,
        setManifests,
        options,
        setOptions
    });
    let content = /*#__PURE__*/ _jsx(Loader, {
        active: true
    });
    if (!manifest) {
        content = /*#__PURE__*/ _jsxs(Message, {
            color: "olive",
            children: [
                "Inaktiv: ",
                i18n.getType(typeInfo && typeInfo.name)
            ]
        });
    }
    if (size(data) > 0) {
        content = /*#__PURE__*/ _jsxs(Segment, {
            tertiary: true,
            attached: true,
            children: [
                /*#__PURE__*/ _jsx(JSONTree, {
                    hideRoot: true,
                    data: data,
                    shouldExpandNode: shouldExpandNode,
                    valueRenderer: valueRenderer
                }),
                /*#__PURE__*/ _jsx(Rail, {
                    attached: true,
                    internal: true,
                    position: "right",
                    children: /*#__PURE__*/ _jsx(Segment, {
                        tertiary: true,
                        attached: true,
                        children: data && /*#__PURE__*/ _jsx(Summary, {
                            i3fdata: data
                        })
                    })
                })
            ]
        });
    }
    return /*#__PURE__*/ _jsx(HeaderSegment, {
        major: true,
        header: i18n.get(`I3F`),
        subheader: manifest,
        icon: "list",
        portal: "I3FDetailsPortal",
        menu: menu,
        className: styles.i3fdetails,
        children: content
    });
};
export const I3FDetails = funcPart(({ i18n })=>/*#__PURE__*/ _jsx(I3FView, {
        i18n: i18n
    }));
